import { Injectable } from '@angular/core';

import { ToastrService } from 'ngx-toastr';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { take } from 'rxjs/operators';

@Injectable()
export class NotificationService {
  constructor(
    private readonly toastr: ToastrService,
    private readonly navigator: NavigationProvider
  ) {}

  onMonitorsLicenseExceeded(): void {
    this.onFail('Active test license count exceeded.');
  }

  onMonitorsDuplicateName(error: string): void {
    this.onFail(error);
  }

  onUnknownCreateMonitorErrorOccurred(): void {
    this.onFail(
      'Something went wrong when creating your active test. Please try again or contact your system administrator.'
    );
  }

  onUnknownCreateAgentErrorOccurred(): void {
    this.onFail(
      'Something went wrong when creating your agent. Please try again or contact your system administrator.'
    );
  }

  onUnknownCreateGigaStorErrorOccurred(): void {
    this.onFail(
      'Something went wrong when creating your gigastor. Please try again or contact your system administrator.'
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onMonitorCreationFailure(data: any): void {
    this.onFail(
      "Something went wrong when creating your active test '" +
        data.alias +
        "'. Please try again or contact your system administrator."
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onMonitorCreationSuccess(data: any): void {
    const monitorUrl = this.getMonitorUrl();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.toastr.success('', '', <any>{
      scope: {
        type: 'onMonitorCreationSuccess',
        alias: data.alias,
        onClick: () => {
          this.navigator.navigateToDashboard(monitorUrl);
        }
      }
    });
  }

  // eslint-disable-next-line
  onMonitorBulkCreationSuccess(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.toastr.success('', '', <any>{
      timeOut: 0,
      scope: {
        type: 'onMonitorBulkCreationSuccess',
        alias: 'test list',
        onClick: () => this.navigator.navigateToDashboard(this.getMonitorUrl())
      }
    });
  }

  // eslint-disable-next-line
  onMonitorBulkCreationFailed(response: any, onClick: Function): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.toastr.error('', '', <any>{
      timeOut: 0,
      scope: {
        type: 'onMonitorBulkCreationFailed',
        alias: 'click here',
        onClick: () => onClick(response)
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onMonitorUpdatingSuccess(data: any): void {
    const monitorUrl = this.getMonitorUrl();
    this.toastr
      //eslint-disable-next-line
      .success('', '', <any>{
        scope: {
          type: 'onMonitorUpdatingSuccess',
          alias: data.alias
        }
      })
      .onTap.pipe(take(1))
      .subscribe(() => this.navigator.navigateToDashboard(monitorUrl));
  }

  // eslint-disable-next-line
  onAgentBuildSuccess(data: any, onClick: Function): void {
    this.toastr
      //eslint-disable-next-line
      .success('', '', <any>{
        disableTimeOut: false,
        scope: {
          alias: data.alias,
          type: 'onAgentBuildSuccess',
          urls: data.urls,
          agentType: data.agentType
        }
      })
      .onTap.pipe(take(1))
      .subscribe(() => onClick(data.id));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onHWAgentBuildSuccess(data: any): void {
    const agentUrl = this.getAgentUrl(data);
    this.toastr
      //eslint-disable-next-line
      .success('', '', <any>{
        tapToDismiss: false,
        scope: {
          type: 'onHWAgentBuildSuccess',
          alias: data.alias
        }
      })
      .onTap.pipe(take(1))
      .subscribe(() => this.navigator.navigateToDashboard(agentUrl));
  }

  // eslint-disable-next-line
  onAgentRegistered(agentEntity: any, onClick: Function): void {
    const agent = agentEntity.getAgent();
    const agentUrl = this.getAgentUrl(agent);
    //eslint-disable-next-line
    this.toastr.success('', '', <any>{
      scope: {
        type: 'onAgentRegistered',
        alias: agentEntity.getAlias(),
        onClick: () => {
          this.navigator.navigateToDashboard(agentUrl);
        },
        onAddMonitorClick: () => onClick(agent)
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onAgentOnline(agentEntity: any): void {
    const agent = agentEntity.getAgent();
    const agentUrl = this.getAgentUrl(agent);
    //eslint-disable-next-line
    this.toastr.success('', '', <any>{
      scope: {
        type: 'onAgentOnline',
        alias: agentEntity.getAlias(),
        onClick: () => {
          this.navigator.navigateToDashboard(agentUrl);
        }
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onAgentOffline(agentEntity: any): void {
    const agent = agentEntity.getAgent();
    const agentUrl = this.getAgentUrl(agent);
    //eslint-disable-next-line
    this.toastr.warning('', '', <any>{
      scope: {
        type: 'onAgentOffline',
        alias: agentEntity.getAlias(),
        onClick: () => {
          this.navigator.navigateToDashboard(agentUrl);
        }
      }
    });
  }

  // eslint-disable-next-line
  onAlert(alert: any, monitor: any): void {
    const monitorUrl = this.getMonitorUrl();
    //eslint-disable-next-line
    this.toastr.error('', '', <any>{
      scope: {
        type: 'onAlert',
        description: alert.description,
        onClick: () => this.navigator.navigateToDashboard(monitorUrl)
      }
    });
  }

  // in case the number of alerts exceeds some sensible value (eg, 5), we can show
  // a single alert message that tells the user the number of alerts he has.
  onNotificationCount(notificationsCount: number): void {
    //eslint-disable-next-line
    this.toastr.error('', '', <any>{
      scope: {
        type: 'onNotificationCount',
        count: notificationsCount,
        onClick: () => this.navigator.navigateToPage(['aiid3a'])
      }
    });
  }

  // in case the number of alerts exceeds some sensible value (eg, 5), we can show
  // a single alert message that tells the user the number of alerts he has.
  onAlertCount(alertsCount: number): void {
    //eslint-disable-next-line
    this.toastr.error('', '', <any>{
      scope: {
        type: 'onAlertCount',
        count: alertsCount,
        onClick: () => this.navigator.navigateToPage(['aiid3a'])
      }
    });
  }

  onPasswordUpdationSuccess(): void {
    this.onSuccess('Password has been updated.');
  }

  onUserInviteSuccess(): void {
    this.onSuccess('New User Invited');
  }

  onGroupAdditionSuccess(): void {
    this.onSuccess('New Group Added');
  }

  onLiveChatNotAvailable(): void {
    this.onFail('Live Chat is currently unavailable');
  }

  onSendPasswordReset(email = ''): void {
    this.onSuccess(`Confirmation link has been sent to registered mail ${email}`);
  }

  onPasswordReset(): void {
    this.onSuccess('Password reset successful. Please log in using new credentials.');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onMonitorStopStateChange(data: any): void {
    const placeholder = this.getPlaceholder(data);
    this.onSuccess(
      `Your test${placeholder}now stopping. Please allow some time for this action to complete.`
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onMonitorStartStateChange(data: any): void {
    const placeholder = this.getPlaceholder(data);
    this.onSuccess(
      `Your test${placeholder}now starting. Please allow some time for this action to complete.`
    );
  }

  // eslint-disable-next-line
  onChipDeleted(onClick: Function): void {
    //eslint-disable-next-line
    this.toastr.success('', '', <any>{
      scope: {
        type: 'onChipDeleted',
        onClick
      }
    });
  }

  //eslint-disable-next-line
  onSuccess(msg: string, title?: string, options?: any): void {
    this.toastr.success(msg, title, options);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onDelaySuccess(msg: string, title?: string, delay?: number, options?: any): void {
    // If delay is provided, set a timeout before showing the notification
    if (delay > 0) {
      setTimeout(() => {
        this.toastr.success(msg, title, options);
      }, delay);
    } else {
      this.toastr.success(msg, title, options);
    }
  }

  onWarning(msg: string): void {
    this.toastr.warning(msg);
  }

  //eslint-disable-next-line
  onFail(msg: string, options?: any): void {
    this.toastr.error(msg, '', options);
  }

  onSuccessWithTitle(msg: string, title: string): void {
    this.toastr.success(msg, title);
  }

  onFailWithTitle(msg: string, title: string): void {
    this.toastr.error(msg, title);
  }

  //Toast help functions
  //eslint-disable-next-line
  private getPlaceholder(data: Array<any>): string {
    return data.length > 1 ? 's are ' : ' is ';
  }

  //eslint-disable-next-line
  private getMonitorUrl(): Array<string> {
    return ['monitor-management'];
  }

  //eslint-disable-next-line
  private getAgentUrl(data: any): Array<string> {
    return ['datasource-management'];
  }
}
